import { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { getConversation, listMessages, sendMessage } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { ArrowLeft, Send, Paperclip, X as XIcon, Image as ImageIcon } from 'lucide-react';
import { logger } from '@/lib/logger';

const POLL_INTERVAL_MS = 3000;
const MAX_ATTACHMENTS = 4;
const MAX_FILE_BYTES = 5 * 1024 * 1024;

const readAsDataURL = (file) => new Promise((resolve, reject) => {
  const r = new FileReader();
  r.onload = () => resolve(r.result);
  r.onerror = reject;
  r.readAsDataURL(file);
});

export default function ChatPage() {
  const { convId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [conv, setConv] = useState(null);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState('');
  const [attachments, setAttachments] = useState([]);
  const [sending, setSending] = useState(false);
  const [previewImg, setPreviewImg] = useState(null);
  const fileRef = useRef(null);
  const endRef = useRef(null);

  // Load conversation header
  useEffect(() => {
    (async () => {
      try {
        const res = await getConversation(convId);
        setConv(res.data);
      } catch (e) {
        toast.error(e.response?.data?.detail || 'Kunne ikke åbne samtalen');
        navigate(-1);
      }
    })();
  }, [convId, navigate]);

  // Poll for new messages
  useEffect(() => {
    let mounted = true;
    const load = async () => {
      try {
        const res = await listMessages(convId);
        if (mounted) setMessages(res.data || []);
      } catch (e) { logger.warn('chat poll failed', e); }
    };
    load();
    const t = setInterval(load, POLL_INTERVAL_MS);
    return () => { mounted = false; clearInterval(t); };
  }, [convId]);

  // Auto-scroll to newest
  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages.length]);

  const otherName = !conv ? '' :
    user?.user_type === 'company' ? conv.user_name : conv.company_name;

  const handleFiles = async (files) => {
    if (!files || files.length === 0) return;
    const arr = Array.from(files);
    const valid = arr.filter(f => {
      if (f.size > MAX_FILE_BYTES) {
        toast.error(`${f.name} er over 5 MB`); return false;
      }
      return true;
    });
    if (attachments.length + valid.length > MAX_ATTACHMENTS) {
      toast.error(`Maks ${MAX_ATTACHMENTS} vedhæftninger pr. besked`);
      return;
    }
    try {
      const data = await Promise.all(valid.map(f => readAsDataURL(f)));
      setAttachments(prev => [...prev, ...data]);
    } catch {
      toast.error('Kunne ikke læse fil');
    }
  };

  const removeAttachment = (idx) => setAttachments(prev => prev.filter((_, i) => i !== idx));

  const handleSend = async (e) => {
    e?.preventDefault?.();
    if (!text.trim() && attachments.length === 0) return;
    setSending(true);
    try {
      const res = await sendMessage(convId, text.trim(), attachments);
      setMessages(prev => [...prev, res.data]);
      setText('');
      setAttachments([]);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Kunne ikke sende besked');
    } finally {
      setSending(false);
    }
  };

  if (!conv) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-stone-50">
        <div className="text-muted-foreground">Indlæser samtale...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-50">
      <div className="max-w-3xl mx-auto px-4 md:px-6 py-6">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-4"
          data-testid="chat-back-button"
        >
          <ArrowLeft className="w-4 h-4" /> Tilbage
        </button>

        <Card className="overflow-hidden flex flex-col" style={{ height: 'calc(100vh - 140px)' }}>
          <CardHeader className="border-b py-4">
            <div className="flex items-center justify-between gap-3">
              <div>
                <CardTitle className="text-base" data-testid="chat-counterparty-name">{otherName}</CardTitle>
                <p className="text-xs text-muted-foreground line-clamp-1">
                  Vedr.: {conv.quote_description}
                </p>
              </div>
              {user?.user_type === 'user' && conv.company_id && (
                <Link
                  to={`/company/${conv.company_id}`}
                  className="text-xs text-[#c97f59] hover:underline"
                  data-testid="chat-view-company-link"
                >
                  Se profil
                </Link>
              )}
            </div>
          </CardHeader>

          {/* Messages */}
          <CardContent className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="chat-messages-list">
            {messages.length === 0 ? (
              <div className="h-full flex items-center justify-center text-center text-sm text-muted-foreground">
                <div>
                  <div className="w-12 h-12 rounded-full bronze-shine mx-auto mb-3 flex items-center justify-center text-white">
                    <Send className="w-5 h-5" />
                  </div>
                  Send den første besked for at uddybe projektet.
                </div>
              </div>
            ) : messages.map((m) => {
              const mine = m.sender_id === user?.id;
              return (
                <div key={m.id} className={`flex ${mine ? 'justify-end' : 'justify-start'}`} data-testid={`chat-message-${m.id}`}>
                  <div className={`max-w-[80%] rounded-2xl px-4 py-2.5 shadow-sm ${mine ? 'bg-primary text-primary-foreground' : 'bg-white text-foreground border border-stone-200'}`}>
                    {!mine && (
                      <p className="text-[10px] font-semibold text-primary mb-1">{m.sender_name}</p>
                    )}
                    {m.attachments?.length > 0 && (
                      <div className="grid grid-cols-2 gap-1 mb-2">
                        {m.attachments.map((src, i) => (
                          <button key={`${m.id || m.created_at}-att-${i}`} onClick={() => setPreviewImg(src)} className="block">
                            <img src={src} alt="vedhæftning" className="rounded-lg w-full h-32 object-cover" />
                          </button>
                        ))}
                      </div>
                    )}
                    {m.text && <p className="text-sm whitespace-pre-wrap break-words">{m.text}</p>}
                    <p className={`text-[10px] mt-1 ${mine ? 'text-primary-foreground/70' : 'text-muted-foreground'}`}>
                      {new Date(m.created_at).toLocaleString('da-DK', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}
                    </p>
                  </div>
                </div>
              );
            })}
            <div ref={endRef} />
          </CardContent>

          {/* Composer */}
          <form onSubmit={handleSend} className="border-t p-3 bg-white">
            {attachments.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-2" data-testid="chat-attachment-previews">
                {attachments.map((src, idx) => (
                  <div key={`att-${src}-${idx}`} className="relative w-16 h-16 rounded-lg overflow-hidden border">
                    <img src={src} alt={`vedhæftning ${idx + 1}`} className="w-full h-full object-cover" />
                    <button
                      type="button"
                      onClick={() => removeAttachment(idx)}
                      className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-black/70 text-white flex items-center justify-center"
                      aria-label="Fjern"
                      data-testid={`remove-attachment-${idx}`}
                    >
                      <XIcon className="w-3 h-3" />
                    </button>
                  </div>
                ))}
              </div>
            )}
            <div className="flex items-end gap-2">
              <input
                ref={fileRef}
                type="file"
                accept="image/*"
                multiple
                className="hidden"
                onChange={(e) => { handleFiles(e.target.files); e.target.value = ''; }}
              />
              <Button
                type="button"
                variant="outline"
                size="icon"
                onClick={() => fileRef.current?.click()}
                data-testid="chat-attach-button"
                aria-label="Vedhæft"
              >
                <Paperclip className="w-4 h-4" />
              </Button>
              <Input
                value={text}
                onChange={(e) => setText(e.target.value)}
                placeholder="Skriv en besked..."
                className="flex-1"
                data-testid="chat-input"
              />
              <Button type="submit" disabled={sending || (!text.trim() && attachments.length === 0)} data-testid="chat-send-button">
                <Send className="w-4 h-4 mr-1.5" />Send
              </Button>
            </div>
          </form>
        </Card>
      </div>

      {previewImg && (
        <div className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-6" onClick={() => setPreviewImg(null)} data-testid="chat-image-preview">
          <button onClick={() => setPreviewImg(null)} className="absolute top-4 right-4 text-white"><XIcon className="w-8 h-8" /></button>
          <img src={previewImg} alt="Forhåndsvisning" className="max-w-full max-h-[90vh] object-contain rounded-lg" />
        </div>
      )}
    </div>
  );
}
